// DemoBeeBackground.tsx - Lively Cosmic Rescue Demo
import React, { useEffect, useRef, useState } from 'react';
import UfoIcon from '../../components/flying-object/UfoIcon';

// Multiple demo paths for different UFOs
const demoPaths = [
  // Main UFO path
  [
    { x: 0.15, y: 0.7, time: 0 },
    { x: 0.25, y: 0.3, time: 800 },
    { x: 0.5, y: 0.15, time: 1600 },
    { x: 0.75, y: 0.3, time: 2400 },
    { x: 0.85, y: 0.7, time: 3200 },
    { x: 0.5, y: 0.85, time: 4000 },
    { x: 0.15, y: 0.7, time: 4800 },
  ],
  // Secondary UFO path (reverse direction)
  [
    { x: 0.8, y: 0.2, time: 0 },
    { x: 0.6, y: 0.6, time: 1000 },
    { x: 0.3, y: 0.4, time: 2000 },
    { x: 0.2, y: 0.8, time: 3000 },
    { x: 0.7, y: 0.9, time: 4000 },
    { x: 0.9, y: 0.5, time: 5000 },
    { x: 0.8, y: 0.2, time: 6000 },
  ],
  // Fast scout UFO
  [
    { x: 0.1, y: 0.1, time: 0 },
    { x: 0.9, y: 0.1, time: 1200 },
    { x: 0.9, y: 0.9, time: 2400 },
    { x: 0.1, y: 0.9, time: 3600 },
    { x: 0.1, y: 0.1, time: 4800 },
  ]
];

// Different emoji types for variety
const emojiTypes = ['🐭', '👼', '🐰', '🦄', '🎶', '❤️‍🔥'];
const ufoTypes = ['basic', 'armored', 'stealth'] as const;

// Floating particle system
interface Particle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  emoji: string;
}

const DemoBeeBackground: React.FC = () => {
  // UFO states
  const [ufos, setUfos] = useState([
    { x: 0, y: 0, rotation: 0, pathIndex: 0, emoji: '🐭', ufoType: 'basic' as const, scale: 1.3, glow: '#FFC107' },
    { x: 0, y: 0, rotation: 0, pathIndex: 1, emoji: '🐱', ufoType: 'armored' as const, scale: 1.2, glow: '#E91E63' },
    { x: 0, y: 0, rotation: 0, pathIndex: 2, emoji: '🦁', ufoType: 'stealth' as const, scale: 1.3, glow: '#00BCD4' },
  ]);

  // Floating rescued emojis
  const [floatingEmojis, setFloatingEmojis] = useState<Particle[]>([]);
  const [showRescueBeam, setShowRescueBeam] = useState(false);
  
  const requestRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const particleIdRef = useRef(0);
  const lastRescueRef = useRef(0);

  // Responsive size
  const [size, setSize] = useState({ width: window.innerWidth, height: window.innerHeight });

  // Easing function for smooth movement
  const easeInOutQuad = (t: number): number => {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
  };

  // Generate floating rescued emoji
  const createFloatingEmoji = (x: number, y: number) => {
    const emoji = emojiTypes[Math.floor(Math.random() * emojiTypes.length)];
    return {
      id: particleIdRef.current++,
      x: x + (Math.random() - 0.5) * 0.1,
      y: y + (Math.random() - 0.5) * 0.1,
      vx: (Math.random() - 0.5) * 0.001,
      vy: -Math.random() * 0.002 - 0.001,
      life: 0,
      maxLife: 3000 + Math.random() * 2000,
      emoji,
    };
  };

  useEffect(() => {
    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTimeRef.current;
      
      // Update UFOs
      setUfos(prevUfos => prevUfos.map((ufo, index) => {
        const path = demoPaths[ufo.pathIndex];
        const pathDuration = path[path.length - 1].time;
        const pathElapsed = elapsed % pathDuration;
        
        let idx = 0;
        while (idx < path.length - 1 && path[idx + 1].time <= pathElapsed) idx++;
        
        const p1 = path[idx];
        const p2 = path[idx + 1] || path[0];
        const segTime = p2.time - p1.time;
        const t = segTime ? easeInOutQuad((pathElapsed - p1.time) / segTime) : 0;
        
        const x = p1.x + (p2.x - p1.x) * t;
        const y = p1.y + (p2.y - p1.y) * t;
        const angle = (Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180 / Math.PI) + 90;
        
        // Add floating motion
        const floatOffset = Math.sin(elapsed * 0.003 + index * 2) * 0.02;
        const bobOffset = Math.cos(elapsed * 0.004 + index) * 0.015;
        
        return {
          ...ufo,
          x: x + floatOffset,
          y: y + bobOffset,
          rotation: angle + Math.sin(elapsed * 0.002 + index) * 10, // Slight wobble
          scale: ufo.scale + Math.sin(elapsed * 0.005 + index) * 0.02, // Pulsing
        };
      }));

      // Create rescue beam effect occasionally
      if (elapsed - lastRescueRef.current > 2500) {
        setShowRescueBeam(true);
        setTimeout(() => setShowRescueBeam(false), 800);
        lastRescueRef.current = elapsed;
        
        // Generate floating emojis at random UFO positions
        setUfos(currentUfos => {
          const randomUfo = currentUfos[Math.floor(Math.random() * currentUfos.length)];
          setFloatingEmojis(prev => [
            ...prev.slice(-8), // Keep only recent 8 emojis
            createFloatingEmoji(randomUfo.x, randomUfo.y),
          ]);
          return currentUfos;
        });
      }

      // Update floating emojis
      setFloatingEmojis(prev => prev
        .map(emoji => ({
          ...emoji,
          x: emoji.x + emoji.vx,
          y: emoji.y + emoji.vy,
          life: emoji.life + 16, // ~60fps
          vy: emoji.vy + 0.00001, // Slight gravity
        }))
        .filter(emoji => emoji.life < emoji.maxLife)
      );

      requestRef.current = requestAnimationFrame(animate);
    };

    startTimeRef.current = Date.now();
    requestRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 100,
        opacity: 0.85,
        transition: 'opacity 0.3s',
        overflow: 'hidden',
      }}
    >
      {/* Cosmic background elements */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `
            radial-gradient(circle at 20% 30%, rgba(147, 51, 234, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.05) 0%, transparent 80%)
          `,
          animation: 'cosmicShift 20s infinite linear',
        }}
      />

      {/* Floating rescued emojis */}
      {floatingEmojis.map(emoji => {
        const progress = emoji.life / emoji.maxLife;
        const opacity = Math.sin(Math.PI * (1 - progress)) * 0.8;
        const scale = 1 + Math.sin(Math.PI * progress) * 0.5;
        
        return (
          <div
            key={emoji.id}
            style={{
              position: 'absolute',
              left: emoji.x * size.width,
              top: emoji.y * size.height,
              transform: `translate(-50%, -50%) scale(${scale})`,
              fontSize: '1.5rem',
              opacity,
              pointerEvents: 'none',
              zIndex: 99,
              filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.6))',
              animation: 'float 2s infinite ease-in-out',
            }}
          >
            {emoji.emoji}
          </div>
        );
      })}

      {/* UFOs */}
      {ufos.map((ufo, index) => (
        <div
          key={index}
          style={{
            position: 'absolute',
            left: ufo.x * size.width,
            top: ufo.y * size.height,
            transform: `translate(-50%, -50%) scale(${ufo.scale}) rotate(${ufo.rotation * 0.1}deg)`,
            pointerEvents: 'none',
            filter: `drop-shadow(0 0 ${20 + index * 5}px ${ufo.glow}cc)`,
            zIndex: 101 + index,
            transition: 'filter 0.3s ease',
          }}
        >
          <UfoIcon emoji={ufo.emoji} ufoType={ufo.ufoType} />
          
          {/* Tractor beam effect for main UFO during rescue */}
          {index === 0 && showRescueBeam && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                width: '60px',
                height: '120px',
                background: `
                  linear-gradient(180deg, 
                    rgba(0, 255, 255, 0.6) 0%, 
                    rgba(0, 255, 255, 0.3) 50%, 
                    rgba(0, 255, 255, 0.1) 100%
                  )
                `,
                transform: 'translateX(-50%)',
                clipPath: 'polygon(40% 0%, 60% 0%, 100% 100%, 0% 100%)',
                animation: 'tractorBeam 0.8s ease-in-out',
                pointerEvents: 'none',
              }}
            />
          )}
        </div>
      ))}

      {/* CSS Animations */}
      <style>{`
        @keyframes cosmicShift {
          0% { transform: rotate(0deg) scale(1); }
          50% { transform: rotate(180deg) scale(1.1); }
          100% { transform: rotate(360deg) scale(1); }
        }
        
        @keyframes float {
          0%, 100% { transform: translate(-50%, -50%) scale(1) translateY(0px); }
          50% { transform: translate(-50%, -50%) scale(1.1) translateY(-10px); }
        }
        
        @keyframes tractorBeam {
          0% { opacity: 0; transform: translateX(-50%) scaleY(0); }
          30% { opacity: 1; transform: translateX(-50%) scaleY(1); }
          70% { opacity: 1; transform: translateX(-50%) scaleY(1); }
          100% { opacity: 0; transform: translateX(-50%) scaleY(0.5); }
        }
      `}</style>
    </div>
  );
};

export default DemoBeeBackground;